import React from 'react';
import { ChamadaPdfData, ChamadaPdfStudent } from '../../../store/actions/frequenciaActions';

type Props = {
  data: ChamadaPdfData | null;
};

const LOGO_URL =
  'https://api-fermi.cientificoeducacao.com.br/api/PersonPics/fermi-images.cientificoeducacao.com.br/download/logoCientifico.png';

const styles: Record<string, React.CSSProperties> = {
  page: {
    fontFamily: 'Arial, sans-serif',
    fontSize: '9px',
    color: '#000',
    padding: '8mm',
    boxSizing: 'border-box',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: '155px 1fr 270px',
    minHeight: '70px',
    border: '1px solid #000',
    marginBottom: '-1px',
  },
  logoArea: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
  },
  logo: {
    maxWidth: '135px',
    maxHeight: '58px',
  },
  headerText: {
    borderLeft: '1px solid #000',
    borderRight: '1px solid #000',
    padding: '6px 8px',
    lineHeight: 1.35,
  },
  schoolText: {
    padding: '6px 8px',
    lineHeight: 1.35,
  },
  titleLine: {
    fontSize: '11px',
    margin: '0 0 8px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  th: {
    border: '1px solid #000',
    padding: '2px',
    height: '52px',
    fontWeight: 'normal',
    verticalAlign: 'bottom',
    textAlign: 'center',
    overflow: 'visible',
  },
  dateTh: {
    border: '1px solid #000',
    padding: '1px',
    height: '52px',
    fontWeight: 'normal',
    verticalAlign: 'top',
    textAlign: 'center',
    position: 'relative',
    overflow: 'visible',
  },
  td: {
    border: '1px solid #000',
    padding: '1px 2px',
    height: '15px',
    lineHeight: '12px',
    textAlign: 'center',
    overflow: 'hidden',
  },
  tdName: {
    border: '1px solid #000',
    padding: '1px 4px',
    height: '15px',
    lineHeight: '12px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  dateText: {
    position: 'absolute',
    top: '25px',
    left: '50%',
    display: 'block',
    transform: 'translateX(-50%) rotate(-90deg)',
    transformOrigin: 'center',
    whiteSpace: 'nowrap',
    fontSize: '6.5px',
    lineHeight: '7px',
    width: '50px',
  },
  freqId: {
    display: 'block',
    fontSize: '4px',
    lineHeight: '5px',
  },
  freqMark: {
    display: 'block',
    fontSize: '8px',
    lineHeight: '8px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    border: '1px solid #000',
    borderTop: 0,
    padding: '4px 6px',
    fontSize: '7px',
  },
  contentPage: {
    breakBefore: 'page',
    pageBreakBefore: 'always',
  },
  contentRow: {
    border: '1px solid #000',
    borderTop: 0,
    minHeight: '15px',
    padding: '2px 4px',
    fontSize: '11px',
  },
};

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function getFrequencyMark(status: number | string): string {
  if (status === 1) return String.fromCharCode(183);
  if (status === 2 || status === 0) return 'F';
  return '';
}

function shouldPrintStudent(student: ChamadaPdfStudent): boolean {
  return true;
}

class ChamadaImpressao extends React.PureComponent<Props> {
  renderHeader(data: ChamadaPdfData) {
    return (
      <div style={styles.header}>
        <div style={styles.logoArea}>
          <img src={LOGO_URL} alt="Logo Cientifico" style={styles.logo} />
        </div>
        <div style={styles.headerText}>
          <p style={styles.titleLine}>{data.disciplina} - {data.etapa}</p>
          <p style={styles.titleLine}>{data.nivel}</p>
          <p style={styles.titleLine}>
            {data.turma} {data.sala} {data.turno} C.H.: {data.cH}h
          </p>
        </div>
        <div style={styles.schoolText}>
          <p style={styles.titleLine}>Colegio Cientifico Porto Seguro</p>
          <p style={styles.titleLine}>Sociedade Educacional Sao Leopoldo Ltda</p>
          <p style={styles.titleLine}>Parecer: {data.schoolAuthorizationOfficialNumber || ''}</p>
        </div>
      </div>
    );
  }

  render() {
    const { data } = this.props;

    if (!data) {
      return <div />;
    }

    const printedStudents = data.alunosdados.filter(shouldPrintStudent);

    return (
      <div>
        <style>
          {`
            @media print {
              @page { size: A4 landscape; margin: 0; }
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          `}
        </style>

        <div style={styles.page}>
          {this.renderHeader(data)}

          <table style={styles.table}>
            <colgroup>
              <col style={{ width: '24px' }} />
              <col style={{ width: '54px' }} />
              <col style={{ width: '220px' }} />
              {data.datas.map((_, index) => (
                <col key={index} style={{ width: '19px' }} />
              ))}
              <col style={{ width: '24px' }} />
              <col style={{ width: '54px' }} />
              <col style={{ width: '62px' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Carne</th>
                <th style={{ ...styles.th, textAlign: 'left', paddingLeft: '4px' }}>Aluno</th>
                {data.datas.map((date, index) => (
                  <th key={`${date || 'vazio'}-${index}`} style={styles.dateTh}>
                    <span style={styles.dateText}>{formatDate(date)}</span>
                  </th>
                ))}
                <th style={styles.th}>#</th>
                <th style={styles.th}>Media</th>
                <th style={styles.th}>Presencas</th>
              </tr>
            </thead>
            <tbody>
              {printedStudents.map((student, index) => (
                <tr key={student.deeId || student.eilId || index}>
                  <td style={styles.td}>{index + 1}</td>
                  <td style={styles.td}>{student.PaymentCard || ''}</td>
                  <td style={styles.tdName}>{student.name || student.Name || ''}</td>
                  {student.frequencia.map((freq, freqIndex) => (
                    <td key={`${freq.id}-${freqIndex}`} style={styles.td}>
                      <span style={styles.freqId}>{freq.id || ''}</span>
                      <span style={styles.freqMark}>{getFrequencyMark(freq.status)}</span>
                    </td>
                  ))}
                  <td style={styles.td}>{index + 1}</td>
                  <td style={styles.td}>{student.Nota ?? ''}</td>
                  <td style={styles.td}>
                    {student.FrequenciaPercentual != null ? `${student.FrequenciaPercentual}%` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={styles.footer}>
            <span>id:{data.header?.id || ''}</span>
            <span>{data.periodo}</span>
          </div>
        </div>

        <div style={{ ...styles.page, ...styles.contentPage }}>
          {this.renderHeader(data)}
          {data.conteudo.map((content, index) => (
            <div key={content.id || index} style={styles.contentRow}>
              {formatDate(content.Date)} - {content.QtyHours || 1}h - {content.Content || ''}
            </div>
          ))}
        </div>
      </div>
    );
  }
}

export default ChamadaImpressao;

export const FunctionalChamadaComponentToPrint = React.forwardRef<
  ChamadaImpressao | null,
  Props
>((props, ref) => {
  return <ChamadaImpressao ref={ref} {...props} />;
});

type BatchProps = {
  chamadas: ChamadaPdfData[];
};

class ChamadaBatchImpressao extends React.PureComponent<BatchProps> {
  render() {
    const { chamadas } = this.props;

    return (
      <div>
        {chamadas.map((chamada, index) => (
          <div
            key={chamada.header?.id || index}
            style={{
              breakAfter: index === chamadas.length - 1 ? 'auto' : 'page',
              pageBreakAfter: index === chamadas.length - 1 ? 'auto' : 'always',
            }}
          >
            <ChamadaImpressao data={chamada} />
          </div>
        ))}
      </div>
    );
  }
}

export const FunctionalChamadaBatchToPrint = React.forwardRef<
  ChamadaBatchImpressao | null,
  BatchProps
>((props, ref) => {
  return <ChamadaBatchImpressao ref={ref} {...props} />;
});
